import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaEye, FaSearch, FaFileAlt, FaUndo } from "react-icons/fa";
import { DatePicker, Space } from "antd";
import { gbState } from "../../../components/shared/GlobalState";
import useFetch from "../../../components/shared/useFetch";
import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import IncomeView from "./IncomeView";

const { RangePicker } = DatePicker;

const IncomeReportsChild = () => {
  const navigate = useNavigate();
  const [{ user }] = useAtom(gbState);
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [incomeData, setIncomeData] = useState([]);
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateValue, setDateValue] = useState(null);
  const [dateInterval, setDateInterval] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const urlLink = `${process.env.REACT_APP_API_URL}/income/${user.church_id}`;
  const { data, loading, error, refetch } = useFetch(urlLink);

  useEffect(() => {
    if (data?.incomes) {
      const updatedData = data.incomes.map((item) => ({
        ...item,
        id: item._id,
        category: item.category_id?.category_name,
      }));
      setIncomeData(updatedData);
      setUnfilteredData(updatedData);
    }
  }, [data]);

  useEffect(() => {
    if (dateValue) {
      setDateInterval({
        sd: new Date(dateValue[0]).getTime(),
        ed: new Date(dateValue[1]).getTime(),
      });
    }
  }, [dateValue]);

  const searchRecordsBetweenDates = () => {
    if (dateInterval.sd && dateInterval.ed) {
      const filteredData = unfilteredData.filter((d) => {
        const time = new Date(d.date).getTime();
        return dateInterval.sd <= time && time <= dateInterval.ed;
      });
      setIncomeData(filteredData);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const resetData = () => {
    setDateValue(null);
    setDateInterval({});
    setIncomeData(unfilteredData);
    setSearchTerm("");
  };

  const filteredData = useMemo(() => {
    return incomeData.filter((income) =>
      Object.values(income).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [incomeData, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Income Reporting</h2>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FaFileAlt />}
          onClick={() => navigate("/income/report", { state: incomeData })}
        >
          Generate Report
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="flex-grow">
          <RangePicker
            value={dateValue}
            onChange={(val) => setDateValue(val)}
            format="MM-DD-YYYY"
          />
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={searchRecordsBetweenDates}
          disabled={!dateInterval.sd}
        >
          Search Date Range
        </Button>
        <Button variant="outlined" startIcon={<FaUndo />} onClick={resetData}>
          Reset Data
        </Button>
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search incomes..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Income Title</TableCell>
              <TableCell>Receipt Number</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Receive Type</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Tooltip title="View details">
                    <IconButton
                      size="small"
                      onClick={() => {
                        toggleModal();
                        setCurrentRecord(row);
                      }}
                    >
                      <FaEye className="text-blue-600" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {new Date(row.date).toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })}
                </TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell>{row.income_title}</TableCell>
                <TableCell>{row.receipt_number}</TableCell>
                <TableCell>{row.amount}</TableCell>
                <TableCell>{row.income_source}</TableCell>
                <TableCell>
                  <span
                    className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                      row.status === "1"
                        ? "bg-orange-400 text-white"
                        : row.status === "2"
                        ? "bg-green-400 text-gray-800"
                        : "bg-red-400 text-white"
                    }`}
                  >
                    {row.status === "1"
                      ? "Pending"
                      : row.status === "2"
                      ? "Settled"
                      : "Declined"}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex items-center justify-between mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/income/" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <IncomeView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default IncomeReportsChild;
